/**
 * CreateBattleModal Component
 *
 * Reusable modal for creating battles.
 * Supports both regular battle creation and challenge mode.
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createBattle } from '../services/battle-api';
import {
  InsufficientCreaturesError,
  ActiveBattleExistsError
} from '../services/battle-api-errors';
import styles from './CreateBattleModal.module.css';

interface ChallengeTarget {
  userId: string;
  displayName: string;
}

interface CreateBattleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** If provided, creates a challenge battle for this user */
  challengeTarget?: ChallengeTarget;
  /** Callback when battle is successfully created */
  onSuccess?: (data: { battleId: string; matchId: string }) => void;
}

export function CreateBattleModal({
  isOpen,
  onClose,
  challengeTarget,
  onSuccess
}: CreateBattleModalProps) {
  const navigate = useNavigate();
  const { getIdToken } = useAuth();

  // Form state
  const [battleName, setBattleName] = useState('');
  const [isPublic, setIsPublic] = useState(false); // Default to private for challenges
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [creatureLimit, setCreatureLimit] = useState<number>(3);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setBattleName('');
      setIsPublic(challengeTarget ? false : true); // Private by default for challenges
      setPassword('');
      setShowPassword(false);
      setCreatureLimit(3);
      setError(null);
    }
  }, [isOpen, challengeTarget]);

  const handleClose = useCallback(() => {
    if (!isCreating) {
      onClose();
    }
  }, [isCreating, onClose]);

  const handleCreate = useCallback(async () => {
    if (!battleName.trim()) {
      setError('Please enter a battle name');
      return;
    }

    // Validate password for closed battles
    if (!isPublic && password.length < 4) {
      setError('Password must be at least 4 characters');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      if (challengeTarget) {
        // Challenge mode - send challenge with battle options
        const token = await getIdToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

        const response = await fetch(`${apiUrl}/api/invites/challenge`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            targetUserId: challengeTarget.userId,
            battleName: battleName.trim(),
            isPublic,
            password: !isPublic ? password : undefined,
            creatureLimit
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to send challenge');
        }

        // Success - navigate to deployment
        onSuccess?.({ battleId: data.battleId, matchId: data.matchId });
        onClose();
        navigate(`/deployment?matchId=${data.matchId}&playerId=player1`);
      } else {
        // Regular battle creation
        const result = await createBattle(
          battleName.trim(),
          isPublic,
          isPublic ? undefined : password,
          creatureLimit
        );

        onSuccess?.({ battleId: result.battleId, matchId: result.matchId });
        onClose();
        navigate(`/deployment?matchId=${result.matchId}&playerId=${result.playerId}`);
      }
    } catch (err) {
      setIsCreating(false);
      if (err instanceof InsufficientCreaturesError) {
        setError(err.message);
      } else if (err instanceof ActiveBattleExistsError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to create battle. Please try again.');
      }
    }
  }, [
    battleName,
    isPublic,
    password,
    creatureLimit,
    challengeTarget,
    getIdToken,
    navigate,
    onClose,
    onSuccess
  ]);

  if (!isOpen) return null;

  const isChallenge = !!challengeTarget;
  const title = isChallenge
    ? `Challenge ${challengeTarget.displayName}`
    : 'Create New Battle';

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.title}>{title}</h2>

        {isChallenge && (
          <p className={styles.challengeInfo}>
            Create a battle and send a challenge to {challengeTarget.displayName}.
            They will receive a notification with the battle details.
          </p>
        )}

        {/* Battle Name */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Battle Name</label>
          <input
            type="text"
            placeholder="Epic Showdown"
            value={battleName}
            onChange={(e) => setBattleName(e.target.value)}
            maxLength={50}
            autoFocus
            disabled={isCreating}
            className={styles.input}
          />
        </div>

        {/* Visibility Toggle */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Visibility</label>
          <div className={styles.visibilityToggle}>
            <label className={`${styles.visibilityOption} ${isPublic ? styles.selected : ''}`}>
              <input
                type="radio"
                name="visibility"
                checked={isPublic}
                onChange={() => setIsPublic(true)}
                disabled={isCreating}
              />
              <span className={styles.visibilityIcon}>🌐</span>
              <div>
                <strong>Open</strong>
                <span className={styles.visibilityDesc}>Anyone can join</span>
              </div>
            </label>
            <label className={`${styles.visibilityOption} ${!isPublic ? styles.selected : ''}`}>
              <input
                type="radio"
                name="visibility"
                checked={!isPublic}
                onChange={() => setIsPublic(false)}
                disabled={isCreating}
              />
              <span className={styles.visibilityIcon}>🔒</span>
              <div>
                <strong>Closed</strong>
                <span className={styles.visibilityDesc}>Password required</span>
              </div>
            </label>
          </div>
        </div>

        {/* Battle Size Selector */}
        <div className={styles.formGroup}>
          <label className={styles.label}>Battle Size</label>
          <div className={styles.battleSizeToggle}>
            {[3, 4, 5, 6].map((num) => (
              <button
                key={num}
                type="button"
                className={`${styles.battleSizeOption} ${creatureLimit === num ? styles.selected : ''}`}
                onClick={() => setCreatureLimit(num)}
                disabled={isCreating}
              >
                {num}v{num}
              </button>
            ))}
          </div>
        </div>

        {/* Password Field (only for closed battles) */}
        {!isPublic && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Password
              {isChallenge && (
                <span className={styles.passwordHint}>
                  (will be shared with {challengeTarget.displayName})
                </span>
              )}
            </label>
            <div className={styles.passwordInputWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password (min 4 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                maxLength={32}
                disabled={isCreating}
                className={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.togglePasswordBtn}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className={styles.error}>{error}</div>
        )}

        <div className={styles.actions}>
          <button
            onClick={handleClose}
            disabled={isCreating}
            className={`${styles.button} ${styles.cancel}`}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating || !battleName.trim() || (!isPublic && password.length < 4)}
            className={`${styles.button} ${styles.create}`}
          >
            {isCreating
              ? 'Creating...'
              : isChallenge
                ? 'Send Challenge'
                : 'Create Battle'}
          </button>
        </div>
      </div>
    </div>
  );
}
